import { FACE_AVATARS, FACE_JOBS, FACE_NAMES, timing, variantForLevel, } from './params';
const FEEDBACK_MS = timing.feedbackMs;
function pickN(arr, n) {
    const pool = [...arr];
    const out = [];
    while (out.length < n && pool.length > 0) {
        const i = Math.floor(Math.random() * pool.length);
        out.push(pool.splice(i, 1)[0]);
    }
    return out;
}
export function buildStimulus(trialIdx, setSize, level) {
    const variant = variantForLevel(level);
    const safeN = Math.max(1, Math.min(Math.round(setSize), Math.min(FACE_AVATARS.length, FACE_NAMES.length)));
    const avatars = pickN(FACE_AVATARS, safeN);
    const names = pickN(FACE_NAMES, safeN);
    const jobs = variant.showJob ? pickN(FACE_JOBS, safeN) : null;
    const faces = avatars.map((a, i) => ({
        id: `${trialIdx}-${i}-${a}-${names[i] ?? '?'}`,
        avatar: a,
        name: names[i] ?? 'Unknown',
        job: jobs ? jobs[i] : undefined,
    }));
    const targetIdx = Math.floor(Math.random() * faces.length);
    const target = faces[targetIdx];
    // Candidats : target + (nCandidates - 1) distracteurs depuis FACE_AVATARS
    // ne faisant pas partie de `faces`.
    const distractorsPool = FACE_AVATARS.filter((a) => !faces.some((f) => f.avatar === a));
    const distractorsAvatars = pickN(distractorsPool, variant.nCandidates - 1);
    const distractorNames = pickN(FACE_NAMES.filter((n) => !faces.some((f) => f.name === n)), variant.nCandidates - 1);
    const distractors = distractorsAvatars.map((a, i) => ({
        id: `${trialIdx}-d${i}-${a}`,
        avatar: a,
        name: distractorNames[i] ?? 'Unknown',
    }));
    const candidates = [...distractors, target].sort(() => Math.random() - 0.5);
    return {
        trialIdx,
        faces,
        targetIdx,
        encodingMsPerFace: variant.encodingMsPerFace,
        candidates,
    };
}
export function createFaceFactsAdapter(level = 1) {
    let state = {
        subPhase: 'idle',
        stim: null,
        encodingIdx: -1,
        feedback: null,
        level,
        variant: variantForLevel(level),
    };
    const listeners = new Set();
    let resolver = null;
    let rejecter = null;
    let trialStart = 0;
    const timers = [];
    function clearTimers() {
        while (timers.length > 0) {
            const t = timers.pop();
            if (t !== undefined)
                clearTimeout(t);
        }
    }
    function update(patch) {
        state = { ...state, ...patch };
        for (const l of listeners) {
            try {
                l(state);
            }
            catch {
                /* ignored */
            }
        }
    }
    function presentFace(stim, idx) {
        if (idx >= stim.faces.length) {
            update({ subPhase: 'pre_test', encodingIdx: -1 });
            timers.push(setTimeout(() => {
                update({ subPhase: 'awaiting_response' });
                trialStart = Date.now();
            }, timing.preTestMs));
            return;
        }
        update({ subPhase: 'encoding', encodingIdx: idx });
        timers.push(setTimeout(() => {
            presentFace(stim, idx + 1);
        }, stim.encodingMsPerFace + timing.betweenEncodingMs));
    }
    return {
        mount() { },
        unmount() {
            clearTimers();
            listeners.clear();
            if (rejecter) {
                rejecter(new Error('unmount'));
                rejecter = null;
                resolver = null;
            }
        },
        presentStimulus(stim) {
            clearTimers();
            return new Promise((resolve, reject) => {
                resolver = resolve;
                rejecter = reject;
                update({
                    subPhase: 'pre_encoding',
                    stim,
                    encodingIdx: -1,
                    feedback: null,
                });
                timers.push(setTimeout(() => {
                    presentFace(stim, 0);
                }, timing.preEncodingMs));
            });
        },
        async showFeedback(correct) {
            update({ subPhase: 'feedback', feedback: correct ? 'correct' : 'incorrect' });
            await new Promise((resolve) => {
                timers.push(setTimeout(() => {
                    update({ subPhase: 'iti' });
                    resolve();
                }, FEEDBACK_MS));
            });
        },
        pause() { },
        resume() { },
        abort() {
            clearTimers();
            if (rejecter) {
                rejecter(new Error('aborted'));
                rejecter = null;
                resolver = null;
            }
        },
        getState() {
            return state;
        },
        subscribe(listener) {
            listeners.add(listener);
            listener(state);
            return () => {
                listeners.delete(listener);
            };
        },
        handlePick(faceId) {
            if (state.subPhase !== 'awaiting_response')
                return;
            const stim = state.stim;
            if (!stim)
                return;
            const target = stim.faces[stim.targetIdx];
            const correct = faceId === target.id;
            const rt = Date.now() - trialStart;
            const resp = { pickedId: faceId, correct, reactionTimeMs: rt };
            if (resolver) {
                const r = resolver;
                resolver = null;
                rejecter = null;
                r(resp);
            }
        },
        setLevel(newLevel) {
            const v = variantForLevel(newLevel);
            update({ level: newLevel, variant: v });
        },
    };
}
//# sourceMappingURL=adapter.js.map